import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calculator, Save, RefreshCw, CheckCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Tab di gestione margine % per categoria. Riusato per:
 *   Prezzo Minimo (markup_percentage)
 *   Prezzo Autorizzare (auth_markup_percentage)
 *   Prezzo Scontato (discount_margin_percentage)
 */
export function MarginTab({
  theme,                    // 'amber' | 'purple' | 'cyan'
  formulaTitle,
  formulaBody,
  visibilityNote,
  categories,
  fieldKey,                 // chiave del campo in cat: markup_percentage etc
  editedValues,
  setEditedValues,
  bulkValue,
  setBulkValue,
  saving,
  hasChanges,
  onSaveSingle,             // (catId) => void
  onSaveAll,                // () => void
  onApplyToProducts,        // () => void
  applying,
  loading,
  saveAllKey,               // 'all' | 'allAuth' | 'allDiscount'
  singleSavingKey,          // (catId) => string
  bulkApplyTestId,
  bulkInputTestId,
  applyButtonTextColor = "black",
}) {
  const themeMap = {
    amber:  { bg: "bg-amber-500/10",  border: "border-amber-500/20",  iconText: "text-amber-500",  textHi: "text-amber-200",  text2: "text-amber-200/70",  inputBorder: "border-white/10",          btnPrimary: "bg-amber-500 hover:bg-amber-600 text-black",   btnGhost: "text-zinc-400 hover:text-amber-400",  btnApply: "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10", spinnerBorder: "border-amber-500" },
    purple: { bg: "bg-purple-500/10", border: "border-purple-500/20", iconText: "text-purple-500", textHi: "text-purple-200", text2: "text-purple-200/70", inputBorder: "border-purple-500/30",     btnPrimary: "bg-purple-500 hover:bg-purple-600 text-white", btnGhost: "text-zinc-400 hover:text-purple-400", btnApply: "border-purple-500/50 text-purple-400 hover:bg-purple-500/10",   spinnerBorder: "border-purple-500" },
    cyan:   { bg: "bg-cyan-500/10",   border: "border-cyan-500/20",   iconText: "text-cyan-500",   textHi: "text-cyan-200",   text2: "text-cyan-200/70",   inputBorder: "border-cyan-500/30",       btnPrimary: "bg-cyan-500 hover:bg-cyan-600 text-black",     btnGhost: "text-zinc-400 hover:text-cyan-400",   btnApply: "border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10",         spinnerBorder: "border-cyan-500" },
  };
  const t = themeMap[theme];

  const handleChange = (catId, value) => {
    setEditedValues(prev => ({ ...prev, [catId]: value === "" ? "" : (parseFloat(value) || 0) }));
  };

  const handleBulkApply = () => {
    const val = parseFloat(bulkValue);
    if (isNaN(val) || val < 0 || val >= 100) return;
    const updated = {};
    categories.forEach(cat => { updated[cat.id] = val; });
    setEditedValues(updated);
    setBulkValue("");
    toast.success(`Margine ${val}% impostato a tutte le categorie`);
  };

  const bulkInvalid = bulkValue === "" || isNaN(bulkValue) || bulkValue < 0 || bulkValue >= 100;

  return (
    <>
      <Card className={`${t.bg} ${t.border}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Calculator className={`w-5 h-5 ${t.iconText} mt-0.5`} />
            <div>
              <p className={`${t.textHi} text-sm`}>
                <strong>{formulaTitle}</strong> {formulaBody}
              </p>
              {visibilityNote && <p className={`${t.text2} text-xs mt-1`}>{visibilityNote}</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex items-center gap-2 bg-[#18181b] border border-white/10 rounded-lg px-3 py-1.5">
          <span className="text-sm text-zinc-400 whitespace-nowrap">Imposta a tutti:</span>
          <Input
            type="number" min="0" max="99" step="0.5"
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
            placeholder="es. 20"
            className={`w-20 text-center bg-[#27272a] ${t.inputBorder} font-mono h-8`}
            data-testid={bulkInputTestId}
          />
          <span className="text-zinc-400 text-sm">%</span>
          <Button
            size="sm"
            disabled={bulkInvalid}
            onClick={handleBulkApply}
            className={`${t.btnPrimary} h-8 px-3`}
            data-testid={bulkApplyTestId}
          >
            Applica
          </Button>
        </div>
        <div className="flex gap-3">
          <Button onClick={onSaveAll} disabled={!hasChanges || saving[saveAllKey]} className={t.btnPrimary}>
            {saving[saveAllKey] ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salva Modifiche
          </Button>
          <Button onClick={onApplyToProducts} disabled={applying} variant="outline" className={t.btnApply}>
            {applying ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Applica ai Prodotti
          </Button>
        </div>
      </div>

      <Card className="bg-[#18181b] border-white/5 overflow-x-auto">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${t.spinnerBorder}`}></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Categoria</TableHead>
                  <TableHead className="text-zinc-400 text-center w-40">Margine %</TableHead>
                  <TableHead className="text-zinc-400 text-right w-24">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((cat) => {
                  const currentVal = cat[fieldKey] ?? "";
                  const editedVal = editedValues[cat.id] ?? "";
                  const isDirty = editedVal !== currentVal;
                  const savingKey = singleSavingKey(cat.id);
                  return (
                    <TableRow key={cat.id} className="border-white/5">
                      <TableCell className="font-medium text-white">{cat.name}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Input
                            type="number" min="0" max="99" step="0.5"
                            value={editedVal}
                            onChange={(e) => handleChange(cat.id, e.target.value)}
                            placeholder={fieldKey === "markup_percentage" ? undefined : "--"}
                            className={`w-24 text-center bg-[#27272a] ${t.inputBorder} font-mono`}
                          />
                          <span className="text-zinc-400">%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => onSaveSingle(cat.id)}
                          disabled={!isDirty || saving[savingKey]}
                          className={t.btnGhost}
                        >
                          {saving[savingKey] ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}
